/* global G */
/* eslint-disable no-nested-ternary */
import { useContext } from 'react'
import ApplicationContext from '@platform/react/context/application'
import { isRemote } from '@platform/adapter/attachment/helper'
import { getMimeGroup, getMimeType } from 'lib/util/attachment'
import { isArr, isBool, isObj } from 'lib/util'
import fileConfig from 'configPlatform/ui/_shared/file.json5'
import typesConfig from 'configPlatform/ui/_shared/types.json5'
import type { Attachment } from '@platform/react/hook/useAttachmentAPI'

// A complex previewable type additionally lists (or, prefixed with `!`,
// excludes) specific mime types of its group
interface ComplexType {
  group: string
  types: string[]
}

type PreviewableType = string | ComplexType

export interface UsePreviewableAttachmentProps {
  attachment: Attachment
  previewableTypes?: string[] | null
  maxPreviewSize?: number | boolean | null
}

/**
 * Checks whether `mimeType` is included in (or, using the `!` prefix,
 * excluded from) the types of the given complex type.
 *
 * @param complexType a complex previewable type
 * @param mimeType    the mime type of the attachment
 * @param group       the extensions of the complex type's mime group
 * @returns           whether the mime type can be previewed
 */
const parseComplexType = (complexType: ComplexType, mimeType: string, group: string[]): boolean => {
  const complexMimeTypes = complexType?.types

  if (!complexMimeTypes || !isArr(complexMimeTypes) || !complexMimeTypes.length) {
    throw new TypeError(`Complex mime types not defined correctly. Got: ${JSON.stringify(complexMimeTypes)}`)
  }

  const foundMimetype = complexMimeTypes.find(x => x.includes(mimeType))

  return foundMimetype
    ? !foundMimetype.includes('!')
    : group.includes(mimeType)
}

/**
 * usePreviewableAttachment Hook
 *
 * Determines whether `attachment` is previewable. It does so by comparing its
 * mime group and `size` with the defaults set either in the application, the
 * current action or the component configuration. If its mime group matches
 * and its `size` does not exceed the maximum preview size, it's considered
 * previewable. Local attachments, meaning attachments that have not been
 * uploaded yet, are always previewable.
 *
 * How do we determine if a type is previewable? There are multiple scenarios:
 * - For an attachment, we have information about its mimetype (`video/mp4`)
 * - For a url, we only have the url itself (`http://.../video.mp4`)
 *
 * The configured previewable types (`['video', 'image', 'txt']`) may contain
 * mime groups as well as mime types, and, in addition to strings, complex
 * objects like `{group: 'text', types: ['!csv', 'txt', ...]}`, where a `!`
 * prefix excludes that type from its group.
 *
 * NOTE: for url-only attachments, only the extension is known, so a group in
 * the previewable types means "every extension of that group is previewable".
 * This is fine for `video`/`image`, but not for groups like `doc` (we cannot
 * display word documents) — add specific mime types instead in that case.
 *
 * @param props            the hook's properties
 * @param props.attachment the attachment at hand
 * @returns                whether the attachment is previewable
 */
const usePreviewableAttachment = ({
  attachment,
  previewableTypes: componentPreviewableTypes = null,
  maxPreviewSize: componentMaxPreviewSize = null,
}: UsePreviewableAttachmentProps): boolean => {
  const {
    session,
    maxPreviewSize: defaultMaxPreviewSize,
    previewableTypes: defaultPreviewableTypes,
  } = useContext(ApplicationContext) as any

  const mimeGroup = getMimeGroup(attachment.value, fileConfig.types)
  const mimeType = getMimeType(attachment.value)

  // Maximum preview size can be declared on a per component, per action or
  // default basis; `maxPreviewSize: true` skips the check entirely
  const maxPreviewSizeConfig = componentMaxPreviewSize
      || session[G.MODULE][G.STATE][G.ACTION][G.COMPONENT][G.PROPS]?.maxPreviewSize
      || defaultMaxPreviewSize
  const maxPreviewSize = isBool(maxPreviewSizeConfig)
    ? Number.MAX_SAFE_INTEGER
    : maxPreviewSizeConfig

  // Types that can be previewed, meaning downloaded immediately, declared on
  // a per component, per action or default basis
  const previewableTypes: PreviewableType[] = componentPreviewableTypes
    || session[G.MODULE][G.STATE][G.ACTION][G.COMPONENT][G.PROPS]?.previewableTypes
    || defaultPreviewableTypes

  const mutualTypes = previewableTypes
    .map(x => typesConfig.find((y: ComplexType) => y.group === x) || x)
    .filter((x: PreviewableType) => (
      isObj(x) ? true : Object.keys(fileConfig.types).includes(x as string)
    ))

  const canBePreviewed = mutualTypes.some((key: PreviewableType) => {
    const isComplexType = isObj(key)
    const complexKey = key as ComplexType
    const groupTypes = fileConfig.types[isComplexType ? complexKey.group : key as string]

    return key !== mimeGroup && key !== mimeType
      && complexKey?.group !== mimeGroup && complexKey?.group !== mimeType
      ? false
      : groupTypes
        ? isComplexType
          ? parseComplexType(complexKey, mimeType as string, groupTypes)
          : groupTypes.includes(mimeType)
        : false
  })

  const size = parseInt(String(attachment.value?.size || 0), 10) / 1024
  const isLocal = !!attachment?.value?.name
    && !isRemote(attachment as Required<Pick<Attachment, 'key' | 'value'>>)

  return (size <= maxPreviewSize && canBePreviewed)
      || attachment?.value?.name === 'avatar'
      || isLocal
}

export default usePreviewableAttachment
